import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { createWarehouseLine, deleteWarehouseLine, getWarehouseLines, updateWarehouseLine } from 'services'
import { svgLoading } from 'stories/assets'
import { confirm, thousandSeperator } from 'utils'

import { EditWarehouseLineDialog } from './EditWarehouseLineDialog'
import type { IWarehouseLine } from './types'

const formatAmount = (value?: number) => {
  if (!value) return '-'
  return `$${thousandSeperator(value)}`
}

const formatPercent = (value?: number) => {
  if (!value && value !== 0) return '-'
  return `${(Number(value) * 100).toFixed(1)}%`
}

export const WarehouseLines = () => {
  const [isLoading, setLoading] = useState(false)
  const [rows, setRows] = useState<IWarehouseLine[]>([])
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [editItem, setEditItem] = useState<Partial<IWarehouseLine> | null>(null)

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const result = await getWarehouseLines()
      setRows(result.results || [])
      setTotals(result.totals || {})
    } catch (error) {
      console.error(error)
      setRows([])
      setTotals({})
    }
    setLoading(false)
  }

  const onSave = async (data: Partial<IWarehouseLine>) => {
    if (editItem?.id) await updateWarehouseLine(editItem.id, data)
    else await createWarehouseLine(data)
    setEditItem(null)
    fetch()
  }

  const onDelete = async (item: IWarehouseLine) => {
    const ok = await confirm(`Delete warehouse line "${item.name}"?`)
    if (!ok) return
    setLoading(true)
    await deleteWarehouseLine(item.id)
    fetch()
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          Warehouse Lines
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 animate-spin" />
            </span>
          )}
        </h1>
        <button className="btn-icon flex items-center gap-1 text-sm" onClick={() => setEditItem({})}>
          <PlusIcon className="w-4 h-4" /> Add
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Outstanding and loan count pull from the Loan Subledger. GL Balance pulls from the Trial Balance. Variance
        (Outstanding − GL) should be $0.
      </p>

      <div className="relative overflow-x-auto">
        <LayoutLoading show={isLoading} />
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="text-xs uppercase bg-gray-100">
            <tr>
              {[
                'Warehouse Line',
                'Bank',
                'GL Acct',
                'Commitment',
                'Adv. Rate',
                'Outstanding',
                'Available',
                'Utilization',
                '# Loans',
                'GL Balance',
                'Variance',
                'Action',
              ].map((h) => (
                <th key={h} className="px-3 py-2 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={12} className="px-3 py-8 text-center text-gray-500">
                  No warehouse lines yet.
                </td>
              </tr>
            )}
            {rows.map((item) => (
              <tr key={item.id} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{item.name}</td>
                <td className="px-3 py-2">{item.bank}</td>
                <td className="px-3 py-2">{item.glAccountNo}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.commitment)}</td>
                <td className="px-3 py-2 text-right">{formatPercent(item.advanceRate)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.outstanding)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.available)}</td>
                <td className="px-3 py-2 text-right">{formatPercent(item.utilization)}</td>
                <td className="px-3 py-2 text-right">{item.loanCount || '-'}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.glBalance)}</td>
                <td className="px-3 py-2 text-right">
                  <span className={Math.abs(item.variance || 0) < 0.01 ? 'text-green-700' : 'text-red-600'}>
                    {formatAmount(item.variance)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-2 items-center">
                    <span
                      className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                      onClick={() => setEditItem(item)}
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                    <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onDelete(item)}>
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="bg-blue-50 font-semibold border-t-2">
                <td colSpan={3} className="px-3 py-2">
                  TOTAL
                </td>
                <td className="px-3 py-2 text-right">{formatAmount(totals.commitment)}</td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right">{formatAmount(totals.outstanding)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(totals.available)}</td>
                <td className="px-3 py-2 text-right">{formatPercent(totals.utilization)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {editItem && <EditWarehouseLineDialog item={editItem} onClose={() => setEditItem(null)} onSave={onSave} />}
    </div>
  )
}
